import React, { useState, useRef, useEffect, useCallback } from 'react';
import API, { getImagenUrl, getSchemaName } from 'shared/api';
import VisorImagen from 'shared/components/VisorImagen';
import {
  FiX,
  FiUpload,
  FiFileText,
  FiPackage,
  FiAlertTriangle,
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiMapPin,
  FiLayers,
  FiPlus,
  FiMinus,
  FiTrash2,
  FiRefreshCw,
  FiImage,
  FiZap,
  FiSearch,
  FiEye,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';

const ESTADO_META = {
  con_existencia: { label: 'Con existencia', color: '#16a34a', bg: '#16a34a14' },
  stock_parcial: { label: 'Existencia parcial', color: '#d97706', bg: '#d9770614' },
  sin_existencia: { label: 'Agotado', color: '#dc2626', bg: '#dc262614' },
  no_encontrado: { label: 'No encontrado', color: '#6b7280', bg: '#6b728014' },
};

const fmtCant = (v, pesable) =>
  pesable ? `${(v / 1000).toFixed(2)} kg` : `${Math.round(v)} und`;

const fmtMonto = (v) =>
  v.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function itemProducto(it) {
  return it._reemplazo || it;
}
function itemPesable(it) {
  return !!itemProducto(it).pesable;
}
function refKeyOf(it) {
  const p = itemProducto(it);
  return String(p.codigo || p.referencia || '').toUpperCase();
}
function stockTotal(it) {
  if (it._reemplazo) {
    const p = it._reemplazo;
    return p.pesable ? Math.floor((p.stock || 0) * 1000) : Math.floor(p.stock || 0);
  }
  return Math.floor(it.stock_disponible || 0);
}
function stockDisponible(it) {
  const p = itemProducto(it);
  if (it._reemplazo) {
    return p.pesable ? Math.floor((p.stock || 0) * 1000) : Math.floor(p.stock || 0);
  }
  return Math.floor(it.stock_disponible || 0);
}
function itemEsCargable(it) {
  return it.estado === 'con_existencia' || it.estado === 'stock_parcial' || !!it._reemplazo;
}
function itemConSugerencias(it) {
  return it.estado === 'sin_existencia' || it.estado === 'no_encontrado' || it.estado === 'stock_parcial';
}
function itemSubtotal(it) {
  const p = itemProducto(it);
  const precio = parseFloat(p.precio || 0);
  const qty = it._qty || 0;
  if (!qty) return 0;
  return p.pesable ? (precio * qty) / 1000 : precio * qty;
}

function itemModificado(it) {
  if (it._reemplazo) return true;
  if (it.estado === 'con_existencia') return (it._qty || 0) !== (it.cantidad_propuesta || 0);
  if (it.estado === 'stock_parcial') return (it._qty || 0) > 0;
  return false;
}

function precioUnitarioLabel(it) {
  const p = itemProducto(it);
  const precio = parseFloat(p.precio || 0);
  if (!precio) return '';
  return p.pesable ? `$${fmtMonto(precio)}/kg` : `$${fmtMonto(precio)}/und`;
}

function inicializarSedes(sedes) {
  return (sedes || []).map((s) => ({
    ...s,
    items: (s.items || []).map((it, idx) => {
      const base = { ...it, _id: `${s.hoja}:${idx}`, _eliminado: false, _reemplazo: null };
      base._qty = it.estado === 'con_existencia' ? (it.cantidad_propuesta || 0) : 0;
      return base;
    }),
  }));
}

function CantidadInput({ value, pesable, max, onChange }) {
  const [texto, setTexto] = useState(() => (value ? (pesable ? (value / 1000).toFixed(2) : String(value)) : '0'));
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setTexto(value ? (pesable ? (value / 1000).toFixed(2) : String(value)) : '0');
  }, [value, pesable, editing]);
  const commit = () => {
    setEditing(false);
    const n = parseFloat(String(texto).replace(',', '.'));
    let val = Number.isFinite(n) ? (pesable ? Math.round(n * 1000) : Math.round(n)) : 0;
    val = Math.max(0, Math.min(max, val));
    onChange(val);
  };
  return (
    <input
      value={texto}
      onFocus={(e) => { setEditing(true); e.target.select(); }}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
      inputMode="decimal"
      style={{ width: 54, textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'var(--agx-text)', border: '1px solid var(--agx-border)', borderRadius: 8, background: 'var(--agx-surface)', padding: '4px 6px', fontFamily: 'inherit' }}
    />
  );
}

export default function ClientesOrdenCompraModal({ cliente, onClose }) {
  const [paso, setPaso] = useState('archivo');
  const [analizando, setAnalizando] = useState(false);
  const [reporte, setReporte] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [error, setError] = useState('');
  const [ejecutando, setEjecutando] = useState(false);
  const [archivoNombre, setArchivoNombre] = useState('');
  const [sedeActiva, setSedeActiva] = useState(0);
  const [resultado, setResultado] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [galIdx, setGalIdx] = useState(0);
  const [visorOpen, setVisorOpen] = useState(null);
  const [sugerenciasDe, setSugerenciasDe] = useState(null);
  const [sugerenciasLista, setSugerenciasLista] = useState([]);
  const [cargandoSugerencias, setCargandoSugerencias] = useState(false);
  const [toast, setToast] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [vistaPrevia, setVistaPrevia] = useState(false);
  const [secciones, setSecciones] = useState({ disponibles: true, faltantes: true });
  const toastTimer = useRef(null);
  const fileRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const schemaName = getSchemaName();
  const persistKey = `agorax_orden_compra_${cliente.id}`;

  const mostrarToast = useCallback((texto, opts = {}) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ texto, undo: opts.undo || null, id: Date.now() });
    toastTimer.current = setTimeout(() => setToast(null), opts.duration || 3500);
  }, []);

  const analizarArchivo = async (file) => {
    if (!file) return;
    setAnalizando(true);
    setError('');
    setReporte(null);
    setSedes([]);
    setSedeActiva(0);
    setBusqueda('');
    setArchivoNombre(file.name);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('idcliente', String(cliente.id));
      const res = await API.post('/orden-compra/analizar', fd);
      if (res.data.status === 'error') {
        setError(res.data.message || 'El archivo no tiene la estructura correcta');
        return;
      }
      const data = res.data.data;
      setReporte(data);
      setSedes(inicializarSedes(data.sedes));
      setPaso('revision');
    } catch (e) {
      setError(e?.response?.data?.detail || e?.response?.data?.message || 'No se pudo leer el archivo');
    } finally {
      setAnalizando(false);
    }
  };

  useEffect(() => {
    if (paso !== 'revision' || !sedes.length) return;
    try {
      localStorage.setItem(persistKey, JSON.stringify({ sedes, sedeActiva, archivoNombre }));
    } catch {}
  }, [sedes, sedeActiva, archivoNombre, paso, persistKey]);

  useEffect(() => {
    if (paso !== 'revision') return;
    try {
      const raw = localStorage.getItem(persistKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.sedes && Array.isArray(saved.sedes) && saved.sedes.length) {
          setSedes(saved.sedes);
          setSedeActiva(saved.sedeActiva || 0);
          setArchivoNombre(saved.archivoNombre || '');
          mostrarToast('Se restauró tu avance anterior');
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiarPersistencia = () => {
    try { localStorage.removeItem(persistKey); } catch {}
  };

  const mutarItem = (hoja, itemId, fn) => {
    setSedes((prev) =>
      prev.map((s) =>
        s.hoja === hoja
          ? { ...s, items: s.items.map((it) => (it._id === itemId ? fn(it) : it)) }
          : s
      )
    );
  };

  const asignadoPorRef = (() => {
    const m = {};
    for (const s of sedes) {
      for (const it of s.items) {
        if (it._eliminado) continue;
        const k = refKeyOf(it);
        m[k] = (m[k] || 0) + (it._qty || 0);
      }
    }
    return m;
  })();

  const maxDisponible = (it) => {
    const total = stockTotal(it);
    const asignadoOtros = (asignadoPorRef[refKeyOf(it)] || 0) - (it._qty || 0);
    return Math.max(0, total - asignadoOtros);
  };

  const cambiarCantidad = (hoja, itemId, delta) => {
    const sede = sedes.find((s) => s.hoja === hoja);
    const it = sede?.items.find((x) => x._id === itemId);
    if (!it) return;
    const max = maxDisponible(it);
    mutarItem(hoja, itemId, (cur) => {
      const sig = Math.max(0, Math.min(max, (cur._qty || 0) + delta));
      return { ...cur, _qty: sig };
    });
  };

  const setCantidad = (hoja, itemId, val) => {
    const sede = sedes.find((s) => s.hoja === hoja);
    const it = sede?.items.find((x) => x._id === itemId);
    if (!it) return;
    const max = maxDisponible(it);
    const n = Math.max(0, Math.min(max, Math.round(val || 0)));
    mutarItem(hoja, itemId, (cur) => ({ ...cur, _qty: n }));
  };

  const eliminarItem = (hoja, itemId) => {
    const sede = sedes.find((s) => s.hoja === hoja);
    const it = sede?.items.find((x) => x._id === itemId);
    if (!it) return;
    const prevQty = it._qty || 0;
    const nombre = itemProducto(it).descripcion || itemProducto(it).nombre || it.referencia;
    mutarItem(hoja, itemId, (cur) => ({ ...cur, _eliminado: true, _qty: 0 }));
    mostrarToast(`"${nombre}" eliminado`, {
      undo: () => mutarItem(hoja, itemId, (cur) => ({ ...cur, _eliminado: false, _qty: prevQty })),
    });
  };

  const quitarFaltantes = (hoja) => {
    let n = 0;
    setSedes((prev) =>
      prev.map((s) => {
        if (s.hoja !== hoja) return s;
        return {
          ...s,
          items: s.items.map((it) => {
            if (!itemEsCargable(it)) {
              n += 1;
              return { ...it, _eliminado: true, _qty: 0 };
            }
            return it;
          }),
        };
      })
    );
    mostrarToast(`Se quitaron ${n} artículos sin existencia`);
  };

  const abrirDetalle = async (codigo) => {
    if (!codigo) return;
    setCargandoDetalle(true);
    setGalIdx(0);
    setDetalle({ codigo });
    try {
      const sede = reporte?.sede_origen || '0101';
      const res = await API.get(`/catalogo/producto/${encodeURIComponent(codigo)}`, {
        params: { sede, schema_name: schemaName },
      });
      setDetalle(res.data?.data || { codigo });
    } catch (e) {
      setDetalle({ codigo, error: 'No se pudo cargar el detalle' });
    } finally {
      setCargandoDetalle(false);
    }
  };

  const abrirSugerencias = async (hoja, itemId) => {
    const sede = sedes.find((s) => s.hoja === hoja);
    const it = sede?.items.find((x) => x._id === itemId);
    if (!it) return;
    setSugerenciasDe({ hoja, itemId });
    setSugerenciasLista([]);
    setCargandoSugerencias(true);
    try {
      const res = await API.post('/orden-compra/sugerencias', {
        referencia: it.referencia,
        descripcion: it.descripcion || '',
        idcliente: cliente.id,
        schema_name: schemaName,
      });
      setSugerenciasLista(res.data?.data?.sugerencias || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.response?.data?.message || 'No se pudo buscar sugerencias');
    } finally {
      setCargandoSugerencias(false);
    }
  };

  const reemplazarCon = (sug) => {
    if (!sugerenciasDe) return;
    const { hoja, itemId } = sugerenciasDe;
    const sede = sedes.find((s) => s.hoja === hoja);
    const it = sede?.items.find((x) => x._id === itemId);
    const qty = it ? Math.min(it.cantidad_solicitada || 0, sug.pesable ? Math.floor((sug.stock || 0) * 1000) : Math.floor(sug.stock || 0)) : 0;
    mutarItem(hoja, itemId, (cur) => ({ ...cur, _reemplazo: sug, _qty: qty }));
    setSugerenciasDe(null);
    setSugerenciasLista([]);
    mostrarToast(`Reemplazado por ${sug.descripcion}`);
  };

  const sugerenciaOriginal = sugerenciasDe
    ? (sedes.find((s) => s.hoja === sugerenciasDe.hoja)?.items || []).find((x) => x._id === sugerenciasDe.itemId) || null
    : null;

  const totales = (() => {
    const t = { total: 0, agregados: 0, monto: 0, con: 0, parcial: 0, sin: 0, no: 0 };
    for (const s of sedes) {
      for (const it of s.items) {
        if (it._eliminado) continue;
        t.total += 1;
        if (it._reemplazo || it.estado === 'con_existencia') t.con += 1;
        else if (it.estado === 'stock_parcial') t.parcial += 1;
        else if (it.estado === 'sin_existencia') t.sin += 1;
        else t.no += 1;
        if (it._qty > 0) {
          t.agregados += 1;
          t.monto += itemSubtotal(it);
        }
      }
    }
    return t;
  })();

  const sede = sedes[sedeActiva];
  const resumenSede = (s) => {
    const r = { total: 0, agregados: 0 };
    for (const it of s.items) {
      if (it._eliminado) continue;
      r.total += 1;
      if (it._qty > 0) r.agregados += 1;
    }
    return r;
  };
  const rSede = sede ? resumenSede(sede) : { total: 0, agregados: 0 };

  const q = (busqueda || '').trim().toLowerCase();
  const sedeItemsVisibles = sede
    ? sede.items.filter((it) => {
        if (it._eliminado) return false;
        if (!q) return true;
        const p = itemProducto(it);
        const hay = `${it.referencia} ${p.codigo || ''} ${p.descripcion || ''} ${p.nombre || ''}`.toLowerCase();
        return hay.includes(q);
      })
    : [];
  const grupoDisponibles = sedeItemsVisibles.filter((it) => itemEsCargable(it));
  const grupoFaltantes = sedeItemsVisibles.filter((it) => !itemEsCargable(it));

  const previewSedes = sedes
    .map((s) => ({
      hoja: s.hoja,
      nombre: s.sucursal_nombre || s.nombre_archivo,
      items: s.items.filter((it) => !it._eliminado && (it._qty || 0) > 0),
      total: s.items
        .filter((it) => !it._eliminado && (it._qty || 0) > 0)
        .reduce((a, it) => a + itemSubtotal(it), 0),
    }))
    .filter((s) => s.items.length > 0);
  const previewExcluidos = sedes.reduce(
    (a, s) => a + s.items.filter((it) => !it._eliminado && (it._qty || 0) === 0).length,
    0
  );

  const renderItemRow = (it) => {
    const p = itemProducto(it);
    const pesable = itemPesable(it);
    const cargable = itemEsCargable(it);
    const max = maxDisponible(it);
    const disponible = stockDisponible(it);
    const img = p.thumb_url || p.imagen_url;
    const nombre = p.descripcion || p.nombre || it.referencia;
    const modificado = itemModificado(it);
    const unitario = precioUnitarioLabel(it);
    return (
      <div key={it._id} style={{ ...S.item, background: modificado ? 'var(--agx-pri2)08' : 'transparent' }}>
        <button onClick={() => p.codigo && abrirDetalle(p.codigo)} style={S.thumb} title="Ver detalle">
          {img && getImagenUrl(img) ? (
            <img src={getImagenUrl(img)} alt="" style={S.thumbImg} onError={(e) => (e.currentTarget.style.display = 'none')} />
          ) : (
            <FiImage size={16} color="var(--agx-muted)" />
          )}
        </button>
        <button onClick={() => p.codigo && abrirDetalle(p.codigo)} style={S.itemInfo}>
          <div style={S.itemName}>
            {nombre}
            {it._reemplazo && <span style={S.reemplazoBadge}><FiRefreshCw size={9} /> reemplazo</span>}
            {!it._reemplazo && it.estado !== 'con_existencia' && (
              <span style={{ ...S.estadoBadge, background: (ESTADO_META[it.estado] || ESTADO_META.no_encontrado).bg, color: (ESTADO_META[it.estado] || ESTADO_META.no_encontrado).color }}>
                {(ESTADO_META[it.estado] || ESTADO_META.no_encontrado).label}
              </span>
            )}
            {modificado && !it._reemplazo && <span style={S.modificadoDot} title="Modificado" />}
          </div>
          <div style={S.itemMeta}>
            <span style={S.itemRef}>{p.codigo || it.referencia}</span>
            <span>· solicita {fmtCant(it.cantidad_solicitada, it.pesable)}</span>
            <span style={{ color: cargable ? '#16a34a' : '#dc2626' }}>· {fmtCant(disponible, pesable)} en existencia</span>
            {!it._reemplazo && it.encontrado && !it.descripcion_coincide && (
              <span style={{ color: '#d97706' }}> · descripción difiere</span>
            )}
            {!it._reemplazo && it.estado === 'stock_parcial' && (
              <span style={{ color: '#d97706' }}> · se asignan {fmtCant(it.cantidad_propuesta, it.pesable)}</span>
            )}
            {unitario && <span style={{ color: 'var(--agx-muted)' }}> · {unitario}</span>}
          </div>
        </button>

        <div style={S.itemRight}>
          {cargable ? (
            <div style={S.stepper}>
              <button onClick={() => cambiarCantidad(sede.hoja, it._id, pesable ? -50 : -1)} disabled={!it._qty} style={S.stepBtn} aria-label="Disminuir">
                <FiMinus size={13} />
              </button>
              <CantidadInput value={it._qty || 0} pesable={pesable} max={max} onChange={(v) => setCantidad(sede.hoja, it._id, v)} />
              <button onClick={() => cambiarCantidad(sede.hoja, it._id, pesable ? 50 : 1)} disabled={it._qty >= max} style={S.stepBtn} aria-label="Aumentar">
                <FiPlus size={13} />
              </button>
            </div>
          ) : itemConSugerencias(it) ? (
            <button onClick={() => abrirSugerencias(sede.hoja, it._id)} style={S.sugerirBtn}>
              <FiRefreshCw size={12} /> Sugerir similares
            </button>
          ) : null}

          <button onClick={() => eliminarItem(sede.hoja, it._id)} style={S.trashBtn} title="Eliminar">
            <FiTrash2 size={14} />
          </button>
        </div>
      </div>
    );
  };

  const renderFaltanteInfoRow = (it) => {
    const p = itemProducto(it);
    const img = p.thumb_url || p.imagen_url;
    const nombre = p.descripcion || p.nombre || it.referencia;
    const meta = ESTADO_META[it.estado] || ESTADO_META.no_encontrado;
    return (
      <div key={it._id} style={S.infoRow}>
        <div style={S.thumb}>
          {img && getImagenUrl(img) ? (
            <img src={getImagenUrl(img)} alt="" style={S.thumbImg} onError={(e) => (e.currentTarget.style.display = 'none')} />
          ) : (
            <FiImage size={16} color="var(--agx-muted)" />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.itemName}>{nombre}</div>
          <div style={S.itemMeta}>
            <span style={S.itemRef}>{p.codigo || it.referencia}</span>
            <span>· solicita {fmtCant(it.cantidad_solicitada, itemPesable(it))}</span>
            {it.estado === 'stock_parcial' && (
              <span style={{ color: '#d97706' }}> · disponible {fmtCant(it.cantidad_propuesta, itemPesable(it))}</span>
            )}
          </div>
        </div>
        <span style={{ ...S.estadoBadge, background: meta.bg, color: meta.color }}>{meta.label}</span>
      </div>
    );
  };

  const ejecutar = async () => {
    if (!reporte) return;
    setEjecutando(true);
    setError('');
    try {
      const sedesPayload = sedes
        .map((s) => ({
          sucursal_id: s.sucursal_id,
          productos: s.items
            .filter((it) => !it._eliminado && (it._qty || 0) > 0)
            .map((it) => {
              const p = itemProducto(it);
              return {
                codigo: p.codigo,
                nombre: p.descripcion || p.nombre,
                precio: p.precio,
                cantidad: it._qty,
                pesable: p.pesable || false,
              };
            }),
        }))
        .filter((s) => s.productos.length > 0);

      if (sedesPayload.length === 0) {
        setError('No hay artículos para cargar');
        return;
      }

      const res = await API.post('/orden-compra/ejecutar', { idcliente: cliente.id, sedes: sedesPayload });
      if (res.data.status === 'success') {
        limpiarPersistencia();
        setResultado(res.data.data);
        setPaso('exito');
      } else {
        setError(res.data.message || 'No se pudo cargar el pedido');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.response?.data?.message || 'Error al cargar el pedido');
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes agxShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          {paso === 'revision' && (
            <button onClick={() => setPaso('archivo')} style={S.iconBtn} title="Volver">
              <FiArrowLeft size={18} />
            </button>
          )}
          <div style={S.iconBox}><FiFileText size={18} color="var(--agx-pri2)" /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.title}>{paso === 'exito' ? 'Pedido cargado' : 'Orden de compra'}</div>
            <div style={S.subtitle}>
              {paso === 'archivo' && cliente.nombre}
              {paso === 'revision' && (archivoNombre || 'Revisa el pedido antes de cargarlo')}
              {paso === 'exito' && 'El pedido se cargó correctamente'}
            </div>
          </div>
          <button onClick={onClose} style={S.iconBtn} title="Cerrar"><FiX size={20} /></button>
        </div>

        {error && (
          <div style={S.error}><FiAlertTriangle size={15} /><span>{error}</span></div>
        )}

        {paso === 'archivo' && (
          <div style={S.body}>
            {analizando ? (
              <div style={S.skeletonWrap}>
                <div style={S.skeletonDrop} />
                <div style={S.skeletonLine} />
                <div style={S.skeletonLine} />
                <div style={{ ...S.skeletonLine, width: '60%' }} />
              </div>
            ) : (
              <>
                <button onClick={() => fileRef.current && fileRef.current.click()} disabled={analizando} style={S.dropzone}>
                  <div style={S.dropIcon}>
                    <FiUpload size={26} color="var(--agx-pri2)" />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--agx-text)' }}>Seleccionar archivo Excel</div>
                  <div style={{ fontSize: 12, color: 'var(--agx-muted)', marginTop: 4 }}>Formato .xlsx / .xls · máximo 10 MB</div>
                </button>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={(e) => e.target.files && analizarArchivo(e.target.files[0])} />

                <div style={S.hint}>
                  <div style={S.hintRow}><FiLayers size={14} /><span>Cada hoja del archivo corresponde a una sucursal.</span></div>
                  <div style={S.hintRow}><FiPackage size={14} /><span>Se cargan los artículos con existencia; el resto puedes reemplazarlo por similares.</span></div>
                  <div style={S.hintRow}><FiRefreshCw size={14} /><span>Puedes ajustar cantidades y eliminar artículos antes de cargar.</span></div>
                </div>
              </>
            )}
          </div>
        )}

        {paso === 'revision' && sede && (
          <div style={S.body}>
            {reporte.advertencia && (
              <div style={S.warning}><FiAlertTriangle size={15} /><span>{reporte.advertencia}</span></div>
            )}

            <div style={S.summary}>
              <div style={S.summaryTop}>
                <span style={S.summaryTitle}>Resumen del pedido</span>
                <span style={S.summaryMeta}>{sedes.length} sucursales · {totales.total} artículos</span>
              </div>
              <div style={S.statsGrid}>
                <Stat label="Con stock" value={totales.con} color="#16a34a" />
                <Stat label="Parcial" value={totales.parcial} color="#d97706" />
                <Stat label="Sin stock" value={totales.sin} color="#dc2626" />
                <Stat label="No encontrado" value={totales.no} color="#6b7280" />
              </div>
              <div style={S.summaryTotal}>
                <div>
                  <div style={S.totalLabel}>Se cargarán</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--agx-text)', lineHeight: 1.1 }}>{totales.agregados} artículos</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={S.totalLabel}>Monto estimado</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--agx-pri2)', lineHeight: 1.1 }}>${fmtMonto(totales.monto)}</div>
                </div>
              </div>
            </div>

            <div style={S.tabs}>
              {sedes.map((s, i) => {
                const r = resumenSede(s);
                const activa = i === sedeActiva;
                const pct = r.total ? Math.round((r.agregados / r.total) * 100) : 0;
                return (
                  <button key={s.hoja} onClick={() => setSedeActiva(i)} style={{ ...S.tab, ...(activa ? S.tabActive : {}) }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={S.tabNum}>{String(s.hoja).padStart(2, '0')}</div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={S.tabLabel}>{s.nombre_archivo || `Sede ${s.hoja}`}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          <div style={S.tabProgressTrack}>
                            <div style={{ ...S.tabProgressFill, width: `${pct}%`, background: pct === 100 ? '#16a34a' : 'var(--agx-pri2)' }} />
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 700, color: activa ? 'var(--agx-pri2)' : 'var(--agx-muted)' }}>
                            {r.agregados}/{r.total}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div style={S.sedeHeader}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.sedeName}><FiMapPin size={13} color="var(--agx-pri2)" />{sede.sucursal_nombre || sede.nombre_archivo}</div>
                <div style={S.sedeSub}>
                  {sede.nombre_archivo && sede.sucursal_nombre && sede.nombre_archivo !== sede.sucursal_nombre
                    ? `Unidad de negocio: ${sede.nombre_archivo}`
                    : `Sucursal ${sede.hoja}`}
                </div>
              </div>
              <div style={S.sedeCount}>
                <span style={{ color: '#16a34a', fontWeight: 700 }}>{rSede.agregados}</span>
                <span style={{ color: 'var(--agx-muted)' }}> / {rSede.total} artículos</span>
              </div>
            </div>

            <div style={S.quickActions}>
              {sede.items.some((it) => !it._eliminado && !itemEsCargable(it)) && (
                <button onClick={() => quitarFaltantes(sede.hoja)} style={{ ...S.quickBtn, color: '#dc2626' }} title="Quitar artículos sin existencia">
                  <FiTrash2 size={13} /> Quitar faltantes
                </button>
              )}
            </div>

            <div style={S.busqueda}>
              <FiSearch size={14} color="var(--agx-muted)" />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por referencia o nombre…"
                style={S.busquedaInput}
              />
              {busqueda && (
                <button onClick={() => setBusqueda('')} style={S.busquedaClear}><FiX size={14} /></button>
              )}
            </div>

            <div style={S.items}>
              {sedeItemsVisibles.length === 0 ? (
                <div style={S.empty}>No se encontraron artículos.</div>
              ) : (
                <>
                  {grupoDisponibles.length > 0 && (
                    <>
                      <button onClick={() => setSecciones((p) => ({ ...p, disponibles: !p.disponibles }))} style={S.seccionHeader}>
                        <span style={{ color: '#16a34a' }}>Disponibles</span>
                        <span style={S.seccionCount}>{grupoDisponibles.length}</span>
                        {secciones.disponibles ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
                      </button>
                      {secciones.disponibles && grupoDisponibles.map(renderItemRow)}
                    </>
                  )}
                  {grupoFaltantes.length > 0 && (
                    <>
                      <button onClick={() => setSecciones((p) => ({ ...p, faltantes: !p.faltantes }))} style={S.seccionHeader}>
                        <span style={{ color: '#dc2626' }}>Sin existencia</span>
                        <span style={S.seccionCount}>{grupoFaltantes.length}</span>
                        {secciones.faltantes ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
                      </button>
                      {secciones.faltantes && (
                        <div style={S.infoNote}>
                          <FiAlertTriangle size={13} style={{ flexShrink: 0 }} />
                          <span>El cliente solicitó estos artículos sin existencia. Solo informativo.</span>
                        </div>
                      )}
                      {secciones.faltantes && grupoFaltantes.map(renderFaltanteInfoRow)}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {paso === 'revision' && sede && (
          <div style={S.footer}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.totalLabel}>Total a cargar</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--agx-text)' }}>
                {totales.agregados} artículos · <span style={{ color: 'var(--agx-pri2)' }}>${fmtMonto(totales.monto)}</span>
              </div>
            </div>
            <button onClick={() => setVistaPrevia(true)} disabled={totales.agregados === 0} style={S.ghostBtn}>
              <FiEye size={15} /> Vista previa
            </button>
            <button onClick={ejecutar} disabled={ejecutando || totales.agregados === 0} style={{ ...S.cta, opacity: ejecutando || totales.agregados === 0 ? 0.5 : 1, cursor: ejecutando || totales.agregados === 0 ? 'not-allowed' : 'pointer' }}>
              {ejecutando ? 'Cargando…' : (
                <>
                  <span>Cargar pedido</span>
                  <span style={S.ctaBadge}>{totales.agregados} · ${fmtMonto(totales.monto)}</span>
                  {!ejecutando && <FiArrowRight size={16} />}
                </>
              )}
            </button>
          </div>
        )}

        {paso === 'exito' && (
          <div style={S.body}>
            <div style={S.success}>
              <div style={S.successIcon}><FiCheck size={28} color="#fff" /></div>
              <div style={S.successTitle}>Pedido cargado</div>
              <div style={S.successText}>
                Se crearon <strong>{resultado?.pedidos?.length || 0}</strong> pedidos — uno por cada sucursal con artículos disponibles.
              </div>
              <button onClick={onClose} style={S.successBtn}>Cerrar</button>
            </div>
          </div>
        )}

        {detalle && (
          <div style={{ ...S.detailOverlay, alignItems: isMobile ? 'flex-end' : 'center' }} onClick={() => setDetalle(null)}>
            <div
              style={{
                ...S.detailModal,
                borderRadius: isMobile ? '20px 20px 0 0' : '20px',
                maxWidth: isMobile ? '480px' : '720px',
                maxHeight: isMobile ? '90vh' : '88vh',
                display: isMobile ? 'block' : 'flex',
                flexWrap: 'wrap',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => setDetalle(null)} style={S.detailClose}><FiX size={22} /></button>

              {cargandoDetalle ? (
                <div style={{ textAlign: 'center', padding: '40px', width: '100%' }}><div style={S.spinner} /></div>
              ) : detalle.error ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--agx-muted)', fontSize: 13, width: '100%' }}>{detalle.error}</div>
              ) : (
                <>
                  <div style={{ ...S.detailMedia, width: isMobile ? '100%' : '44%' }}>
                    <div
                      style={S.detailImage}
                      onClick={() => {
                        const imgs = detalle.imagenes?.length
                          ? detalle.imagenes
                          : getImagenUrl(detalle.imagen_url)
                            ? [{ url: detalle.imagen_url }]
                            : [];
                        if (imgs.length) setVisorOpen({ images: imgs, startIdx: galIdx });
                      }}
                    >
                      {detalle.imagenes?.length > 0 && detalle.imagenes[galIdx].tipo !== 'video' && (
                        <img src={getImagenUrl(detalle.imagenes[galIdx].url)} alt={detalle.descripcion} style={S.detailImg} onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                      {detalle.imagenes?.length > 0 && detalle.imagenes[galIdx].tipo === 'video' && (
                        <video key={detalle.imagenes[galIdx].url} src={getImagenUrl(detalle.imagenes[galIdx].url)} controls playsInline style={{ width: '100%', minHeight: '200px', background: '#000', borderRadius: '12px' }} />
                      )}
                      {!detalle.imagenes?.length && getImagenUrl(detalle.imagen_url) && (
                        <img src={getImagenUrl(detalle.thumb_url || detalle.imagen_url)} alt={detalle.descripcion} style={S.detailImg} onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                      {!detalle.imagenes?.length && !getImagenUrl(detalle.imagen_url) && (
                        <FiPackage size={64} color="var(--agx-pri2)" />
                      )}
                      {(detalle.imagenes?.length > 0 || getImagenUrl(detalle.imagen_url)) && (
                        <span style={S.expandBadge} title="Ampliar imagen">
                          <FiSearch size={13} /> Ampliar
                        </span>
                      )}
                    </div>
                    {detalle.imagenes?.length > 1 && (
                      <div style={S.thumbs}>
                        {detalle.imagenes.map((img, idx) => (
                          <button key={idx} onClick={() => setGalIdx(idx)} style={{ ...S.thumbBtn, border: galIdx === idx ? '2px solid var(--agx-pri2)' : '2px solid transparent' }}>
                            {img.tipo === 'video' ? (
                              <div style={S.thumbVideo}><span style={{ color: '#fff', fontSize: '18px' }}>&#9654;</span></div>
                            ) : (
                              <img src={getImagenUrl(img.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ ...S.detailBody, width: isMobile ? '100%' : '56%' }}>
                    <h2 style={S.detailName}>{detalle.descripcion}</h2>

                    {detalle.descripcion_completa && detalle.descripcion_completa !== detalle.descripcion && (
                      <p style={S.detailDesc}>{detalle.descripcion_completa}</p>
                    )}
                    {detalle.descripcion_corta && (
                      <p style={S.detailDesc}>{detalle.descripcion_corta}</p>
                    )}

                    {detalle.en_promocion && (
                      <div style={S.promoBadge}><FiZap size={14} /> OFERTA -{detalle.porcentaje_descuento}%</div>
                    )}
                    <div style={S.detailPrices}>
                      {detalle.precio_oculto ? (
                        <span style={S.detailPrecioOculto}>Precio oculto hasta que te aprueben</span>
                      ) : (
                        <>
                          <span style={S.detailPriceUsd}>${fmtMonto(detalle.precio || 0)}</span>
                          {detalle.en_promocion && detalle.precio_original != null && (
                            <span style={{ fontSize: '14px', color: 'var(--agx-muted)', textDecoration: 'line-through' }}>
                              ${fmtMonto(detalle.precio_original || 0)}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    <div style={S.detailTags}>
                      {detalle.pesable && <span style={S.tagYellow}>Vendido por peso</span>}
                      {(detalle.tags || []).map((t) => (
                        <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '8px', background: t.color || '#0B1A4A', color: '#fff' }}>
                          {t.icono && <span>{t.icono}</span>}
                          {t.nombre}
                        </span>
                      ))}
                      {!detalle.precio_oculto && (
                        <span style={{ ...S.tagGreen, backgroundColor: detalle.stock > 20 ? '#e8f5e9' : detalle.stock > 0 ? '#fff3e0' : '#fee2e2', color: detalle.stock > 20 ? '#0d904f' : detalle.stock > 0 ? '#ea8600' : '#dc2626' }}>
                          {detalle.stock > 20
                            ? `${detalle.stock} en stock`
                            : detalle.stock > 0
                              ? `Solo ${detalle.stock} disponibles`
                              : 'Agotado'}
                        </span>
                      )}
                    </div>

                    <div style={S.detailMeta}>
                      {detalle.marca && <div style={S.metaItem}><span style={S.metaLabel}>Marca</span><span style={S.metaValue}>{detalle.marca}</span></div>}
                      {detalle.departamento && <div style={S.metaItem}><span style={S.metaLabel}>Departamento</span><span style={S.metaValue}>{detalle.departamento}</span></div>}
                      {detalle.grupo && <div style={S.metaItem}><span style={S.metaLabel}>Grupo</span><span style={S.metaValue}>{detalle.grupo}</span></div>}
                      {detalle.subgrupo && <div style={S.metaItem}><span style={S.metaLabel}>Subgrupo</span><span style={S.metaValue}>{detalle.subgrupo}</span></div>}
                      {detalle.referencia && <div style={S.metaItem}><span style={S.metaLabel}>Referencia</span><span style={S.metaValue}>{detalle.referencia}</span></div>}
                      <div style={S.metaItem}><span style={S.metaLabel}>Código</span><span style={S.metaValue}>{detalle.codigo}</span></div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {sugerenciasDe && (
          <div style={S.detailOverlay} onClick={() => { setSugerenciasDe(null); setSugerenciasLista([]); }}>
            <div style={{ ...S.detailModal, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
              <div style={S.sugHeader}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--agx-text)' }}>Sugerir productos similares</div>
                  <div style={{ fontSize: 12, color: 'var(--agx-muted)' }}>Reemplaza el artículo sin existencia</div>
                </div>
                <button onClick={() => { setSugerenciasDe(null); setSugerenciasLista([]); }} style={S.iconBtn}><FiX size={18} /></button>
              </div>

              <div style={{ padding: '0 16px 16px' }}>
                {sugerenciaOriginal && (
                  <div style={S.sugOriginal}>
                    <div style={S.thumb}>
                      {(() => {
                        const op = itemProducto(sugerenciaOriginal);
                        const oi = op.thumb_url || op.imagen_url;
                        return oi && getImagenUrl(oi) ? (
                          <img src={getImagenUrl(oi)} alt="" style={S.thumbImg} onError={(e) => (e.currentTarget.style.display = 'none')} />
                        ) : (
                          <FiImage size={16} color="var(--agx-muted)" />
                        );
                      })()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--agx-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Solicitado
                      </div>
                      <div style={S.itemName}>{itemProducto(sugerenciaOriginal).descripcion || itemProducto(sugerenciaOriginal).nombre || sugerenciaOriginal.referencia}</div>
                      <div style={S.itemMeta}>
                        <span style={S.itemRef}>{itemProducto(sugerenciaOriginal).codigo || sugerenciaOriginal.referencia}</span>
                        <span>· {fmtCant(sugerenciaOriginal.cantidad_solicitada, itemPesable(sugerenciaOriginal))}</span>
                      </div>
                    </div>
                    <span style={{ ...S.estadoBadge, background: '#dc262614', color: '#dc2626' }}>Agotado</span>
                  </div>
                )}

                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--agx-muted)', textTransform: 'uppercase', letterSpacing: 0.4, margin: '10px 2px 4px' }}>
                  {cargandoSugerencias ? 'Buscando alternativas…' : `${sugerenciasLista.length} alternativa${sugerenciasLista.length !== 1 ? 's' : ''} con existencia`}
                </div>

                {cargandoSugerencias ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
                    <div style={S.skeletonLine} />
                    <div style={S.skeletonLine} />
                    <div style={{ ...S.skeletonLine, width: '70%' }} />
                  </div>
                ) : sugerenciasLista.length === 0 ? (
                  <div style={{ padding: '24px 10px', textAlign: 'center', color: 'var(--agx-muted)', fontSize: 13 }}>
                    No se encontraron productos similares con existencia.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {sugerenciasLista.map((sug) => (
                      <div key={sug.codigo} style={S.sugRow}>
                        <div style={S.thumb}>
                          {sug.thumb_url || sug.imagen_url ? (
                            <img src={getImagenUrl(sug.thumb_url || sug.imagen_url)} alt="" style={S.thumbImg} onError={(e) => (e.currentTarget.style.display = 'none')} />
                          ) : (
                            <FiImage size={16} color="var(--agx-muted)" />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={S.itemName}>{sug.descripcion}</div>
                          <div style={S.itemMeta}>
                            <span style={S.itemRef}>{sug.codigo}</span>
                            {sug.marca && <span>· {sug.marca}</span>}
                            {sug.departamento && <span>· {sug.departamento}</span>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--agx-pri2)' }}>${fmtMonto(sug.precio)}</span>
                            <span style={S.stockChip}>{fmtCant(sug.stock * (sug.pesable ? 1000 : 1), sug.pesable)} disp.</span>
                          </div>
                        </div>
                        <button onClick={() => reemplazarCon(sug)} style={S.reemplazarBtn}>
                          <FiCheck size={13} /> Reemplazar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Vista previa ── */}
        {vistaPrevia && (
          <div style={S.detailOverlay} onClick={() => setVistaPrevia(false)}>
            <div style={{ ...S.detailModal, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
              <div style={S.sugHeader}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--agx-text)' }}>Vista previa del pedido</div>
                  <div style={{ fontSize: 12, color: 'var(--agx-muted)' }}>Resumen de lo que se cargará</div>
                </div>
                <button onClick={() => setVistaPrevia(false)} style={S.iconBtn}><FiX size={18} /></button>
              </div>
              <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '4px 16px 16px' }}>
                {previewSedes.length === 0 ? (
                  <div style={{ padding: '24px 10px', textAlign: 'center', color: 'var(--agx-muted)', fontSize: 13 }}>
                    No hay artículos seleccionados.
                  </div>
                ) : (
                  previewSedes.map((s) => (
                    <div key={s.hoja} style={S.pvSede}>
                      <div style={S.pvSedeHeader}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <span style={S.pvSedeNum}>{String(s.hoja).padStart(2, '0')}</span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--agx-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {s.nombre}
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--agx-muted)' }}>{s.items.length} artículos</div>
                          </div>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--agx-pri2)' }}>${fmtMonto(s.total)}</span>
                      </div>
                      <div style={{ marginTop: 2 }}>
                        {s.items.map((it) => {
                          const p = itemProducto(it);
                          const img = p.thumb_url || p.imagen_url;
                          return (
                            <div key={it._id} style={S.pvItem}>
                              <div style={S.pvThumb}>
                                {img && getImagenUrl(img) ? (
                                  <img src={getImagenUrl(img)} alt="" style={S.thumbImg} onError={(e) => (e.currentTarget.style.display = 'none')} />
                                ) : (
                                  <FiImage size={14} color="var(--agx-muted)" />
                                )}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--agx-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {p.descripcion || p.nombre || it.referencia}
                                </div>
                                <div style={{ fontSize: 10, color: 'var(--agx-muted)', fontFamily: 'monospace' }}>{p.codigo}</div>
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--agx-muted)', whiteSpace: 'nowrap' }}>{fmtCant(it._qty, itemPesable(it))}</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--agx-text)', width: 76, textAlign: 'right' }}>${fmtMonto(itemSubtotal(it))}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div style={{ padding: '12px 16px', borderTop: '1px solid var(--agx-border)', background: 'var(--agx-bg)' }}>
                {previewExcluidos > 0 && (
                  <div style={{ fontSize: 11, color: '#d97706', marginBottom: 8 }}>
                    {previewExcluidos} artículo{previewExcluidos !== 1 ? 's' : ''} sin existencia no se cargará{previewExcluidos !== 1 ? 'n' : ''}.
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={S.totalLabel}>Total general</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--agx-text)' }}>{totales.agregados} artículos</div>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--agx-pri2)' }}>${fmtMonto(totales.monto)}</div>
                </div>
                <button onClick={() => { setVistaPrevia(false); ejecutar(); }} disabled={ejecutando} style={{ ...S.cta, width: '100%', justifyContent: 'center', marginTop: 12 }}>
                  {ejecutando ? 'Cargando…' : 'Confirmar y cargar pedido'}
                  {!ejecutando && <FiArrowRight size={16} />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Toast ── */}
        {toast && (
          <div style={S.toast}>
            <span style={{ flex: 1 }}>{toast.texto}</span>
            {toast.undo && (
              <button onClick={() => { toast.undo(); setToast(null); }} style={S.toastUndo}>Deshacer</button>
            )}
          </div>
        )}

        {/* ── Visor de imagen ampliada ── */}
        {visorOpen && (
          <VisorImagen
            images={visorOpen.images}
            currentIndex={visorOpen.startIdx || 0}
            onClose={() => setVisorOpen(null)}
          />
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={S.stat}>
      <div style={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--agx-muted)', fontWeight: 600, marginTop: 3 }}>{label}</div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: 'var(--agx-surface)', borderRadius: 18, width: '100%', maxWidth: 640, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px', borderBottom: '1px solid var(--agx-border)', flexShrink: 0 },
  iconBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 10, border: 'none', background: 'var(--agx-bg)', color: 'var(--agx-muted)', cursor: 'pointer' },
  iconBox: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: 'var(--agx-pri2)18' },
  title: { fontSize: 16, fontWeight: 800, color: 'var(--agx-text)', lineHeight: 1.2 },
  subtitle: { fontSize: 12, color: 'var(--agx-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  body: { padding: '16px 18px 20px', overflowY: 'auto', flex: 1, minHeight: 0 },

  error: { display: 'flex', gap: 8, alignItems: 'flex-start', margin: '12px 18px 0', padding: '10px 12px', borderRadius: 10, background: '#dc262614', color: '#dc2626', fontSize: 13 },

  dropzone: { width: '100%', padding: '40px 20px', borderRadius: 16, border: '2px dashed var(--agx-border)', background: 'var(--agx-bg)', cursor: 'pointer', textAlign: 'center', fontFamily: 'inherit' },
  dropIcon: { width: 56, height: 56, margin: '0 auto 12px', borderRadius: 16, background: 'var(--agx-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  spinner: { width: 22, height: 22, borderRadius: '50%', border: '3px solid var(--agx-border)', borderTopColor: 'var(--agx-pri2)', animation: 'spin 0.8s linear infinite', margin: '0 auto' },
  hint: { marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10, padding: 14, borderRadius: 12, background: 'var(--agx-bg)' },
  hintRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--agx-muted)' },

  warning: { display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, padding: '10px 12px', borderRadius: 10, background: '#d9770614', color: '#b45309', fontSize: 12 },

  summary: { border: '1px solid var(--agx-border)', borderRadius: 14, padding: 14, marginBottom: 14 },
  summaryTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  summaryTitle: { fontSize: 13, fontWeight: 700, color: 'var(--agx-text)' },
  summaryMeta: { fontSize: 11, color: 'var(--agx-muted)' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 },
  stat: { padding: '10px 8px', borderRadius: 10, background: 'var(--agx-bg)', textAlign: 'center' },
  summaryTotal: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--agx-border)' },
  totalLabel: { fontSize: 11, color: 'var(--agx-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 },

  tabs: { display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 12, scrollbarWidth: 'thin' },
  tab: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, padding: '9px 12px', borderRadius: 12, border: '1px solid var(--agx-border)', background: 'var(--agx-surface)', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--agx-muted)' },
  tabActive: { borderColor: 'var(--agx-pri2)', background: 'var(--agx-pri2)12', boxShadow: '0 0 0 1px var(--agx-pri2)' },
  tabNum: { fontSize: 11, fontWeight: 800, color: 'var(--agx-pri2)', background: 'var(--agx-pri2)18', borderRadius: 6, padding: '2px 6px' },
  tabLabel: { fontSize: 13, fontWeight: 700, color: 'inherit', lineHeight: 1.2 },
  tabProgressTrack: { width: 48, height: 4, borderRadius: 2, background: 'var(--agx-border)', overflow: 'hidden' },
  tabProgressFill: { height: '100%', borderRadius: 2, transition: 'width 0.25s ease' },

  sedeHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '0 2px' },
  sedeName: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700, color: 'var(--agx-text)' },
  sedeSub: { fontSize: 11, color: 'var(--agx-muted)', marginTop: 2 },
  sedeCount: { fontSize: 12, color: 'var(--agx-muted)', flexShrink: 0 },

  quickActions: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' },
  quickBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 10, border: '1px solid var(--agx-border)', background: 'var(--agx-surface)', color: 'var(--agx-pri2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },

  items: { display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--agx-border)' },
  item: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 2px', borderBottom: '1px solid var(--agx-border)', borderRadius: 8, transition: 'background 0.15s' },
  infoRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 2px', borderBottom: '1px solid var(--agx-border)' },
  infoNote: { display: 'flex', alignItems: 'flex-start', gap: 7, padding: '8px 10px', marginBottom: 6, borderRadius: 8, background: '#dc26260d', color: '#b45309', fontSize: 11 },
  thumb: { width: 46, height: 46, borderRadius: 10, overflow: 'hidden', flexShrink: 0, background: 'var(--agx-bg)', border: '1px solid var(--agx-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
  itemInfo: { flex: 1, minWidth: 0, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', padding: 0, fontFamily: 'inherit' },
  itemName: { fontSize: 13, fontWeight: 600, color: 'var(--agx-text)', lineHeight: 1.3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  reemplazoBadge: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 700, color: '#16a34a', background: '#16a34a14', borderRadius: 6, padding: '2px 6px' },
  estadoBadge: { display: 'inline-flex', alignItems: 'center', fontSize: 9, fontWeight: 700, borderRadius: 6, padding: '2px 6px' },
  modificadoDot: { width: 7, height: 7, borderRadius: '50%', background: '#d97706', flexShrink: 0 },
  itemMeta: { fontSize: 11, color: 'var(--agx-muted)', marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 2 },
  itemRef: { fontFamily: 'monospace', color: 'var(--agx-muted)' },
  itemRight: { flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 },

  stepper: { display: 'flex', alignItems: 'center', gap: 2, background: 'var(--agx-bg)', borderRadius: 10, padding: 2 },
  stepBtn: { width: 26, height: 26, borderRadius: 8, border: 'none', background: 'var(--agx-surface)', color: 'var(--agx-text)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  stepVal: { minWidth: 42, textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'var(--agx-text)' },
  sugerirBtn: { display: 'flex', alignItems: 'center', gap: 5, padding: '7px 10px', borderRadius: 10, border: '1px solid var(--agx-border)', background: 'var(--agx-bg)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, color: 'var(--agx-pri2)' },
  trashBtn: { width: 30, height: 30, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--agx-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },

  busqueda: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 12, border: '1px solid var(--agx-border)', background: 'var(--agx-bg)', marginBottom: 10 },
  busquedaInput: { flex: 1, border: 'none', background: 'transparent', fontSize: 13, color: 'var(--agx-text)', fontFamily: 'inherit', outline: 'none' },
  busquedaClear: { border: 'none', background: 'transparent', color: 'var(--agx-muted)', cursor: 'pointer', padding: 2, display: 'flex' },
  empty: { padding: '20px 10px', textAlign: 'center', color: 'var(--agx-muted)', fontSize: 13 },
  seccionHeader: { width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 4px', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid var(--agx-border)' },
  seccionCount: { fontSize: 11, fontWeight: 700, color: 'var(--agx-muted)', background: 'var(--agx-bg)', borderRadius: 999, padding: '1px 8px', marginLeft: 'auto', marginRight: 4 },

  footer: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderTop: '1px solid var(--agx-border)', background: 'var(--agx-surface)', flexShrink: 0 },
  ghostBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '12px 16px', borderRadius: 12, border: '1px solid var(--agx-border)', background: 'var(--agx-surface)', color: 'var(--agx-text)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  cta: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', borderRadius: 12, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, var(--agx-pri1, #1a73e8), var(--agx-pri2, #60a5fa))', color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  ctaBadge: { background: 'rgba(255,255,255,0.2)', padding: '3px 9px', borderRadius: 8, fontSize: 12, fontWeight: 800 },

  skeletonWrap: { display: 'flex', flexDirection: 'column', gap: 12 },
  skeletonDrop: { height: 160, borderRadius: 16, background: 'linear-gradient(90deg, var(--agx-bg) 25%, var(--agx-surface) 50%, var(--agx-bg) 75%)', backgroundSize: '200% 100%', animation: 'agxShimmer 1.4s ease infinite' },
  skeletonLine: { height: 14, borderRadius: 8, background: 'linear-gradient(90deg, var(--agx-bg) 25%, var(--agx-surface) 50%, var(--agx-bg) 75%)', backgroundSize: '200% 100%', animation: 'agxShimmer 1.4s ease infinite' },

  toast: { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 400, display: 'flex', alignItems: 'center', gap: 12, background: 'var(--agx-pri1, #1a73e8)', color: '#fff', padding: '12px 16px', borderRadius: 14, fontSize: 13, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', maxWidth: 'calc(100vw - 32px)' },
  toastUndo: { border: 'none', background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 800, fontSize: 12, padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' },

  success: { textAlign: 'center', padding: '30px 10px 20px' },
  successIcon: { width: 64, height: 64, margin: '0 auto 16px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--agx-pri1, #1a73e8), var(--agx-pri2, #60a5fa))', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  successTitle: { fontSize: 18, fontWeight: 800, color: 'var(--agx-text)' },
  successText: { fontSize: 13, color: 'var(--agx-muted)', marginTop: 6, lineHeight: 1.5 },
  successBtn: { marginTop: 20, padding: '12px 24px', borderRadius: 12, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, var(--agx-pri1, #1a73e8), var(--agx-pri2, #60a5fa))', color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' },

  detailOverlay: { position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(15,23,42,0.55)', display: 'flex', justifyContent: 'center', padding: 20 },
  detailModal: { position: 'relative', background: 'var(--agx-surface)', borderRadius: 18, width: '100%', maxHeight: '86vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' },
  detailClose: { position: 'absolute', top: 14, right: 14, zIndex: 3, width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'var(--agx-bg)', color: 'var(--agx-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  detailMedia: {},
  detailImage: { position: 'relative', width: '100%', height: 260, borderRadius: 14, backgroundColor: 'var(--agx-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'pointer' },
  detailImg: { width: '100%', height: '100%', objectFit: 'contain' },
  expandBadge: { position: 'absolute', bottom: 10, right: 10, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 999, background: 'rgba(15,23,42,0.7)', color: '#fff', fontSize: 11, fontWeight: 700, backdropFilter: 'blur(4px)' },
  thumbs: { display: 'flex', gap: 8, marginTop: 10, overflowX: 'auto', paddingBottom: 4 },
  thumbBtn: { width: 56, height: 56, borderRadius: 10, overflow: 'hidden', flexShrink: 0, padding: 0, cursor: 'pointer', background: 'var(--agx-bg)' },
  thumbVideo: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a1a' },
  detailBody: { padding: '20px 22px 24px' },
  detailName: { margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--agx-text)', lineHeight: 1.25 },
  detailDesc: { fontSize: 13, color: 'var(--agx-muted)', lineHeight: 1.5, margin: '8px 0 0' },
  promoBadge: { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, #c5221f 0%, #ea4335 100%)', color: '#fff', fontWeight: 800, fontSize: 13, padding: '6px 14px', borderRadius: 20, marginTop: 12, marginBottom: 8, boxShadow: '0 3px 8px rgba(197,34,31,0.3)' },
  detailPrices: { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, marginTop: 8 },
  detailPriceUsd: { fontSize: 22, fontWeight: 700, color: 'var(--agx-pri2)' },
  detailPrecioOculto: { fontSize: 14, fontWeight: 600, color: 'var(--agx-muted)' },
  detailTags: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 },
  tagYellow: { fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 8, backgroundColor: '#fef7e0', color: '#b06000' },
  tagGreen: { fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 8 },
  detailMeta: { display: 'flex', flexDirection: 'column', gap: 10, padding: 12, backgroundColor: 'var(--agx-bg)', borderRadius: 12 },
  metaItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  metaLabel: { fontSize: 12, color: 'var(--agx-muted)' },
  metaValue: { fontSize: 12, fontWeight: 600, color: 'var(--agx-text)' },

  sugHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--agx-border)' },
  sugOriginal: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px', marginTop: 12, borderRadius: 12, background: '#dc26260d', border: '1px dashed #dc262633' },
  sugRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--agx-border)' },
  stockChip: { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: '#16a34a14', color: '#16a34a' },
  reemplazarBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '7px 10px', borderRadius: 10, border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 },

  pvSede: { marginTop: 12, border: '1px solid var(--agx-border)', borderRadius: 12, overflow: 'hidden' },
  pvSedeHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 12px', background: 'var(--agx-bg)' },
  pvSedeNum: { fontSize: 11, fontWeight: 800, color: 'var(--agx-pri2)', background: 'var(--agx-pri2)18', borderRadius: 6, padding: '3px 7px' },
  pvItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: '1px solid var(--agx-border)' },
  pvThumb: { width: 36, height: 36, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'var(--agx-bg)', border: '1px solid var(--agx-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
};
